/**
 * Menú del «+» por rol (mandato founder, oct-2026). Cada atajo abre una pantalla que YA existe.
 * `soon: true` = 🔒 «Próximamente», deshabilitado. Desde oct-2026 los atajos de dinero (tarjeta de regalo, cursos,
 * productos, torneos pagos) abren sus pantallas reales: el candado legal lo aplica el BACKEND por carril
 * (Admin → Carriles de pago; nacen apagados en producción) y la pantalla muestra el mensaje si está cerrado.
 * Lo que mueve dinero de la plataforma (depósitos, retiros, tokens, interruptores) NO va aquí: vive en el Panel Admin.
 * Regla M1: productos solo los venden Marcas; barberos venden cursos solo si están en el top del ranking.
 * `role` = rol de VISTA («Ver como»); `identity` = rol real (founder conserva atajos founder-only al ver como admin).
 */
import {
  Image as ImageIcon, Aperture, Radio, BookmarkPlus, Trophy, GraduationCap, Package, UserPlus,
  Scissors, Star, Flame, Gift, Megaphone, Newspaper, BellRing, Medal, Building2, QrCode,
} from 'lucide-react';
import { tr } from '@/contexts/LanguageContext';

const ADMIN_ROLES = new Set(['admin', 'super_admin', 'founder_admin']);
export const CREATE_FAB_ROLES = new Set(['barber', 'barbershop', 'client', 'brand', ...ADMIN_ROLES]);

const barberCreative = (a) => [
  { id: 'post',      label: tr("Publicación / Reel"),      hint: tr("Foto o video al feed"),          icon: ImageIcon,    run: a.openPost },
  { id: 'story',     label: tr("Crear Historia (24 h)"),   hint: tr("Desaparece en un día"),          icon: Aperture,     run: a.openStory },
  { id: 'live',      label: tr("Transmitir en Vivo"),      hint: tr("Abre la pestaña En vivo"),       icon: Radio,        run: () => a.go('/feed?tab=stream'), accent: 'live' },
  { id: 'highlight', label: tr("Historia destacada"),      hint: tr("Círculo fijo en tu perfil"),     icon: BookmarkPlus, run: a.openHighlight },
];

export const buildCreateSections = ({ role, identity, isBarber, flags = {} }, a) => {
  if (role === 'barber') {
    return [{
      id: 'barber',
      items: [
        ...barberCreative(a),
        { id: 'review_link', label: tr("Pedir reseña"),        hint: tr("QR o enlace para tu cliente"),          icon: QrCode,        run: a.openReviewLink },
        { id: 'campaign',    label: tr("Campaña a mis clientes"), hint: tr("Promoción por correo y en la app · confirmas antes"), icon: Megaphone, run: () => a.go('/mis-campanas') },
        { id: 'tournament',  label: tr("Inscribirse a un torneo"), hint: tr("Torneos disponibles"),               icon: Trophy,        run: () => a.go('/tournaments') },
        { id: 'course',      label: tr("Crear curso"),          hint: tr("Solo barberos del top del ranking"),    icon: GraduationCap, run: () => a.go('/seller/courses') },
      ],
    }];
  }

  if (role === 'barbershop') {
    const items = [
      { id: 'post',      label: tr("Publicación / Reel"),   hint: tr("Contenido del local"),               icon: ImageIcon,    run: a.openPost },
      { id: 'highlight', label: tr("Historia destacada"),   hint: tr("Círculo fijo en el perfil del local"), icon: BookmarkPlus, run: a.openHighlight },
      { id: 'campaign',  label: tr("Campaña a mis clientes"), hint: tr("Promoción por correo y en la app · confirmas antes"), icon: Megaphone, run: () => a.go('/shop/campanas') },
    ];
    if (flags.work_requests) {
      items.push({ id: 'invite', label: tr("Invitar barbero"), hint: tr("Sumar un barbero al local"), icon: UserPlus, run: () => a.go('/shop/invitar') });
    }
    items.push({ id: 'tournament_request', label: tr("Solicitar torneo"), hint: tr("Propón un torneo (con o sin premio) · lo revisa Ziyup"), icon: Trophy, run: () => a.go('/shop/solicitar-torneo') });
    return [{ id: 'shop', items }];
  }

  if (role === 'client') {
    return [{
      id: 'client',
      items: [
        { id: 'book',     label: tr("Reservar cita"),     hint: tr("Barberos cerca de ti y reserva"), icon: Scissors, run: () => a.go('/barberos/cerca'), primary: true },
        { id: 'review',   label: tr("Dejar una reseña"),  hint: tr("Califica a tu barbero, con foto"), icon: Star,     run: () => a.go('/reservas?review=1') },
        { id: 'vote',     label: tr("Votar en un reto"),  hint: tr("Reto comunitario activo"),         icon: Flame,    run: a.voteChallenge },
        { id: 'giftcard', label: tr("Enviar tarjeta de regalo"), hint: tr("$10 · $25 · $50 · $100 para un corte"), icon: Gift, run: () => a.go('/regalo/tarjeta') },
      ],
    }];
  }

  if (role === 'brand') {
    return [{
      id: 'brand',
      items: [
        { id: 'brand_post', label: tr("Post de marca (orgánico)"), icon: Building2, run: () => a.openFeed('brand_post') },
        { id: 'product',    label: tr("Añadir producto"),          hint: tr("Tu tienda de Marca verificada"), icon: Package, run: () => a.go('/seller/products') },
      ],
    }];
  }

  if (ADMIN_ROLES.has(role)) {
    const comms = [
      { id: 'mass_notif', label: tr("Notificación masiva"), hint: tr("A todos o por rol · llega a la campana · confirmas antes"), icon: BellRing, run: () => a.go('/admin?tab=avisos') },
      { id: 'campaign', label: tr("Nueva campaña segmentada"), hint: tr("A barberos por ciudad/ranking · requiere tu aprobación"), icon: Megaphone, run: () => a.go('/admin?tab=ia&sub=campaigns') },
      { id: 'announce', label: 'Anuncio oficial', hint: tr("Publicar en el feed como Ziyup"), icon: Newspaper, run: a.openPost },
    ];
    const sections = [
      { id: 'comms', title: tr("Comunicación"), items: comms },
      {
        id: 'compete', title: tr("Competencia y comunidad"), items: [
          { id: 'tournament_create', label: tr("Crear torneo"), hint: tr("Con o sin premio en dinero · la inscripción paga depende del carril 8"), icon: Trophy, run: () => a.go('/admin?tab=tournaments&create=1') },
          { id: 'league_create',     label: tr("Crear liga / temporada"), hint: tr("Ligas por temporada"),          icon: Medal, run: () => a.go('/admin?tab=leagues&create=1') },
          { id: 'challenge_create',  label: tr("Crear reto global"),      hint: tr("Retos de comunidad (Panel Admin)"), icon: Flame, run: () => a.go('/admin?tab=challenges') },
        ],
      },
    ];
    // «Como barbero»: el founder también atiende clientes; un admin sin perfil de barbero no puede crear destacadas (403).
    if (isBarber || identity === 'super_admin' || identity === 'founder_admin') {
      sections.push({
        id: 'as_barber', title: tr("Como barbero"), items: [
          { id: 'post',        label: tr("Publicación / Reel"), hint: tr("Foto o video al feed"),      icon: ImageIcon,    run: a.openPost },
          { id: 'highlight',   label: tr("Historia destacada"), hint: tr("Círculo fijo en tu perfil"), icon: BookmarkPlus, run: a.openHighlight },
          { id: 'review_link', label: tr("Pedir reseña"),       hint: tr("QR o enlace para tu cliente"), icon: QrCode,     run: a.openReviewLink },
        ],
      });
    }
    return sections;
  }

  return [];
};
